<template>
  <div class="gallery-page page-wrapper">
    <div class="page-header">
      <div class="container">
        <h1 class="page-title">照片画廊</h1>
        <p class="page-subtitle">Photo Gallery</p>
      </div>
    </div>

    <div class="page-content">
      <div class="container">
        <!-- 分类筛选 -->
        <div class="gallery-filter">
          <div 
            v-for="category in categories" 
            :key="category.id"
            class="filter-item"
            :class="{ 'active': activeCategory === category.id }"
            @click="onCategoryChange(category.id)"
          >
            {{ category.name }}
          </div>
        </div>

        <!-- 照片网格 -->
        <div class="gallery-grid" v-loading="loading" v-if="filteredPhotos.length > 0 || loading">
          <div 
            v-for="(photo, index) in filteredPhotos" 
            :key="photo.id" 
            class="gallery-item"
            @click="openLightbox(index)"
          >
            <div class="photo-wrapper">
              <img :src="photo.thumb || photo.image" :alt="photo.title" />
              <div class="photo-overlay">
                <i class="el-icon-zoom-in"></i>
              </div>
            </div>
            <div class="photo-info">
              <h4 class="photo-title">{{ photo.title }}</h4>
              <p class="photo-desc" v-if="photo.description">{{ photo.description }}</p>
            </div>
          </div>
        </div>

        <div v-else-if="!loading" class="empty-state">
          <i class="el-icon-picture-outline"></i>
          <p>暂无照片</p>
        </div>

        <!-- 灯箱 -->
        <transition name="fade">
          <div v-if="lightboxVisible" class="lightbox" @click="closeLightbox">
            <div class="lightbox-content" @click.stop>
              <img :src="currentPhoto.image" :alt="currentPhoto.title" />
              <div class="lightbox-info">
                <h3>{{ currentPhoto.title }}</h3>
                <p v-if="currentPhoto.description">{{ currentPhoto.description }}</p>
              </div>
              <button class="lightbox-close" @click="closeLightbox">
                <i class="el-icon-close"></i>
              </button>
              <button class="lightbox-prev" @click.stop="prevPhoto" v-if="currentIndex > 0">
                <i class="el-icon-arrow-left"></i>
              </button>
              <button class="lightbox-next" @click.stop="nextPhoto" v-if="currentIndex < filteredPhotos.length - 1">
                <i class="el-icon-arrow-right"></i>
              </button>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import { galleryApi } from '@/api'

export default {
  name: 'Gallery',
  data() {
    return {
      activeCategory: 0,
      lightboxVisible: false,
      currentIndex: 0,
      loading: false,
      categories: [{ id: 0, name: '全部' }],
      photos: []
    }
  },
  computed: {
    filteredPhotos() {
      if (this.activeCategory === 0) {
        return this.photos
      }
      return this.photos.filter(p => p.category_id === this.activeCategory)
    },
    currentPhoto() {
      return this.filteredPhotos[this.currentIndex] || {}
    }
  },
  created() {
    this.fetchCategories()
    this.fetchPhotos()
  },
  methods: {
    async fetchCategories() {
      try {
        const res = await galleryApi.getCategories()
        const list = (res.data && res.data) || []
        this.categories = [{ id: 0, name: '全部' }, ...list]
      } catch (e) {
        console.error('获取分类失败', e)
      }
    },
    async fetchPhotos() {
      this.loading = true
      try {
        const params = { conference_id: 1 }
        if (this.activeCategory !== 0) {
          params.category_id = this.activeCategory
        }
        const res = await galleryApi.getList(params)
        this.photos = (res.data && res.data.list) || []
      } catch (e) {
        console.error('获取照片失败', e)
      } finally {
        this.loading = false
      }
    },
    onCategoryChange(categoryId) {
      this.activeCategory = categoryId
      this.fetchPhotos()
    },
    openLightbox(index) {
      this.currentIndex = index
      this.lightboxVisible = true
      document.body.style.overflow = 'hidden'
    },
    closeLightbox() {
      this.lightboxVisible = false
      document.body.style.overflow = ''
    },
    prevPhoto() {
      if (this.currentIndex > 0) {
        this.currentIndex--
      }
    },
    nextPhoto() {
      if (this.currentIndex < this.filteredPhotos.length - 1) {
        this.currentIndex++
      }
    }
  },
  beforeDestroy() {
    document.body.style.overflow = ''
  }
}
</script>

<style lang="scss" scoped>
.gallery-page {
  background: #f8f9fc;
}

.gallery-filter {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 40px;
  flex-wrap: wrap;
}

.filter-item {
  padding: 12px 28px;
  background: #fff;
  border-radius: 25px;
  cursor: pointer;
  font-size: 15px;
  color: #555;
  transition: all 0.3s;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);

  &:hover {
    color: #1a237e;
    transform: translateY(-2px);
  }

  &.active {
    background: linear-gradient(135deg, #1a237e 0%, #3949ab 100%);
    color: #fff;
  }
}

.gallery-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
}

.gallery-item {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(26, 35, 126, 0.15);

    .photo-overlay {
      opacity: 1;
    }

    img {
      transform: scale(1.1);
    }
  }
}

.photo-wrapper {
  position: relative;
  height: 200px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s;
  }

  .photo-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(26, 35, 126, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s;

    i {
      font-size: 36px;
      color: #fff;
    }
  }
}

.photo-info {
  padding: 16px;

  .photo-title {
    font-size: 15px;
    font-weight: 600;
    color: #1a237e;
    margin-bottom: 6px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .photo-desc {
    font-size: 13px;
    color: #999;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin: 0;
  }
}

.empty-state {
  text-align: center;
  padding: 80px 0;
  color: #999;

  i {
    font-size: 80px;
    margin-bottom: 20px;
  }

  p {
    font-size: 18px;
  }
}

// 灯箱
.lightbox {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.lightbox-content {
  position: relative;
  max-width: 90%;
  max-height: 90%;

  img {
    max-width: 100%;
    max-height: 80vh;
    border-radius: 8px;
  }
}

.lightbox-info {
  position: absolute;
  bottom: -60px;
  left: 0;
  right: 0;
  text-align: center;
  color: #fff;

  h3 {
    font-size: 18px;
    margin-bottom: 8px;
  }

  p {
    font-size: 14px;
    opacity: 0.8;
  }
}

.lightbox-close {
  position: absolute;
  top: -50px;
  right: 0;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  color: #fff;
  font-size: 20px;
  cursor: pointer;
  transition: background 0.3s;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
}

.lightbox-prev, .lightbox-next {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  color: #fff;
  font-size: 24px;
  cursor: pointer;
  transition: background 0.3s;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
}

.lightbox-prev {
  left: -70px;
}

.lightbox-next {
  right: -70px;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

@media screen and (max-width: 1200px) {
  .gallery-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media screen and (max-width: 768px) {
  .gallery-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  .photo-wrapper {
    height: 150px;
  }

  .lightbox-prev, .lightbox-next {
    display: none;
  }
}

@media screen and (max-width: 480px) {
  .gallery-grid {
    grid-template-columns: 1fr;
  }

  .photo-wrapper {
    height: 200px;
  }
}
</style>
